import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTonAddress, useTonConnectUI } from '@tonconnect/ui-react';
import { Address, beginCell } from '@ton/core';
import {
  BellRing,
  CheckCircle2,
  Gift,
  KeyRound,
  Lock,
  Send,
  Sparkles,
  Users,
  Wallet,
  XCircle,
} from 'lucide-react';
import type { PyramidaUiState } from '../hooks/usePyramidaState';
import { readUserByAddress } from '../ton/contract';
import {
  buildAttributedBotLink,
  isTelegramMiniApp,
  sanitizeReferrerKey,
} from '../ton/referral';
import {
  canShareToStory,
  fetchPublicQuests,
  fetchQuestVoucher,
  fetchQuests,
  resolveOutgoingReferrerKey,
  reportRewardsClaimed,
  reportStoryQuestShared,
  shareReferralStory,
  type PendingClaimItem,
  type QuestItem,
  type VoucherState,
} from '../ton/notify';
import { gaEvent } from '../analytics';
import { SupportLink } from './SupportLink';

interface QuestsProps {
  state: PyramidaUiState;
  /** Switch to the Partners tab (referral tools live there). */
  onGoPartner: () => void;
}

/** Collapses the payout state machine into the 4 user-facing statuses. */
type UserStatus = 'available' | 'queued' | 'paid' | 'rejected';

function userStatus(claimStatus: string | null): UserStatus {
  switch (claimStatus) {
    case 'pending':
    case 'sending':
    case 'send_unknown':
    case 'sent_unconfirmed':
    case 'claimable': // earned; waiting on the contract for the user to claim
      return 'queued';
    case 'sent':
      return 'paid';
    case 'rejected':
    case 'failed':
    case 'banned':
      return 'rejected';
    default:
      return 'available';
  }
}

/** Formats a nano-PRMD amount as a whole PRMD string ("1000"). */
function prmdAmount(nano: string): string {
  const s = nano.padStart(10, '0');
  const int = s.slice(0, -9);
  const frac = s.slice(-9).replace(/0+$/, '');
  return int + (frac ? `.${frac}` : '');
}

/** "H:MM:SS" (hours unbounded) for the maturity countdown. */
function formatCountdown(seconds: number): string {
  const s = Math.max(seconds, 0);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${h}:${pad(m)}:${pad(sec)}`;
}

const RING_RADIUS = 46;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

/**
 * The PRMD coin (same artwork as CoinFlip) that fronts the claim banner.
 * While rewards are maturing, a progress ring FILLS around it over the real
 * maturity window (granted → eligible): full circle = ready to claim.
 */
function ClaimCoin({ remaining, span }: { remaining: number | null; span: number | null }) {
  const maturing = remaining != null && remaining > 0;
  const fraction =
    maturing && span != null && span > 0
      ? Math.min(Math.max((span - remaining) / span, 0), 1)
      : 0;
  return (
    <div className={`claim-coin${maturing ? ' claim-coin--maturing' : ''}`}>
      <img className="claim-coin__img" src="/games/coinflip/coin-front.webp" alt="" />
      {maturing && (
        <svg className="claim-coin__ring" viewBox="0 0 100 100" aria-hidden="true">
          <circle className="claim-coin__ring-track" cx="50" cy="50" r={RING_RADIUS} />
          <circle
            className="claim-coin__ring-fill"
            cx="50"
            cy="50"
            r={RING_RADIUS}
            strokeDasharray={RING_CIRCUMFERENCE}
            strokeDashoffset={RING_CIRCUMFERENCE * (1 - fraction)}
          />
        </svg>
      )}
    </div>
  );
}

/** The quest text in the active UI language, falling back to en → ru → any. */
function questText(texts: Record<string, string>, lang: string): string {
  const base = lang.toLowerCase().split('-')[0];
  return texts[base] ?? texts.en ?? texts.ru ?? Object.values(texts)[0] ?? '';
}

export function Quests({ state, onGoPartner }: QuestsProps) {
  const { t, i18n } = useTranslation();
  const address = useTonAddress();
  const [tonConnectUI] = useTonConnectUI();
  const [quests, setQuests] = useState<QuestItem[] | null | 'loading'>('loading');
  const inMiniApp = isTelegramMiniApp();

  // The public catalog is visible to everyone (plain website included); inside
  // Telegram the authenticated list additionally carries this user's statuses.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const items = inMiniApp ? (await fetchQuests()) ?? (await fetchPublicQuests()) : await fetchPublicQuests();
      if (!cancelled) setQuests(items);
    };
    void load();
    return () => {
      cancelled = true;
    };
    // Re-fetch once the wallet connects: the subscribe+wallet quest needs it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [address, inMiniApp]);

  const me = state.me;
  const refKey = me?.registered && me.key != null ? me.key : null;
  const registered = me?.registered === true;
  // With a wallet connected, "not registered" is a fact only after the
  // identity read succeeded; before that (or when it failed) it is a default.
  const identityKnown = !address || me?.identityKnown === true;
  const [entrySent, setEntrySent] = useState(false);

  // Earned rewards wait on the QuestClaim contract until the user pulls them:
  // the bot signs his cumulative total, the wallet transaction pays the gas.
  // `pending` is the maturing tail (review delay) shown behind the countdown.
  const [voucherState, setVoucherState] = useState<VoucherState>({ voucher: null, pending: null });
  const { voucher, pending } = voucherState;
  const [claimSent, setClaimSent] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void fetchQuestVoucher().then((next) => {
      if (!cancelled) setVoucherState(next);
    });
    return () => {
      cancelled = true;
    };
  }, [address, quests]);

  // One-second heartbeat for the maturity countdown; when the earliest reward
  // matures, ask the bot again — the voucher then covers it.
  const [nowSec, setNowSec] = useState(() => Math.floor(Date.now() / 1000));
  const maturesAt = pending?.eligibleAt ?? null;
  // The ring fills over the true maturity window (granted → eligible); if the
  // grant time is unknown, fall back to the remaining time at first sight.
  const ringWindowRef = useRef<{ at: number; span: number } | null>(null);
  useEffect(() => {
    if (maturesAt == null) {
      ringWindowRef.current = null;
      return;
    }
    const remaining = maturesAt - Math.floor(Date.now() / 1000);
    if (!ringWindowRef.current || ringWindowRef.current.at !== maturesAt) {
      const started = pending?.startedAt ?? null;
      const span = started != null && started < maturesAt ? maturesAt - started : remaining;
      ringWindowRef.current = { at: maturesAt, span: Math.max(span, 1) };
    }
    if (remaining <= 0) return;
    const timer = window.setInterval(() => {
      const now = Math.floor(Date.now() / 1000);
      setNowSec(now);
      if (now >= maturesAt) {
        window.clearInterval(timer);
        void fetchQuestVoucher().then(setVoucherState);
      }
    }, 1000);
    return () => window.clearInterval(timer);
  }, [maturesAt, pending?.startedAt]);

  // The rewards list: ready-to-claim rows first, then maturing ones (older
  // bots without `items` degrade to a single aggregate maturing row). A
  // voucher without matching ready rows still gets a claim row of its own.
  const pendingItems: PendingClaimItem[] =
    pending == null
      ? []
      : pending.items ?? [
          { questId: '', amountNano: pending.amountNano, eligibleAt: pending.eligibleAt },
        ];
  const claimItems: PendingClaimItem[] =
    voucher != null && BigInt(voucher.claimableNano) > 0n && !pendingItems.some((it) => it.ready)
      ? [
          {
            questId: '',
            amountNano: voucher.claimableNano,
            eligibleAt: null,
            ready: true,
          },
          ...pendingItems,
        ]
      : pendingItems;

  /** The maturing row's label: the quest's localized title. */
  const pendingQuestTitle = (questId: string): string => {
    if (Array.isArray(quests)) {
      const quest = quests.find((q) => q.id === questId);
      if (quest) return questText(quest.titles, i18n.language);
    }
    return t('quests.maturingItem');
  };

  /** Redeems every earned-but-unpaid PRMD in one transaction. */
  const handleClaimRewards = async () => {
    if (!voucher) return;
    const body = beginCell()
      .storeUint(voucher.schema === 'ec' ? 0x45434c32 : 0x51434c30, 32) // EcClaim / Claim
      .storeUint(0n, 64)
      .storeCoins(BigInt(voucher.totalNano))
      .storeUint(voucher.validUntil, 32)
      .storeBuffer(Buffer.from(voucher.signature, 'base64'))
      .endCell();
    try {
      await tonConnectUI.sendTransaction({
        validUntil: Math.min(voucher.validUntil, Math.floor(Date.now() / 1000) + 300),
        messages: [
          {
            address: voucher.contract,
            amount: voucher.valueNano,
            payload: body.toBoc().toString('base64'),
          },
        ],
      });
      setClaimSent(true);
      void reportRewardsClaimed();
      gaEvent('quest_claim_sent', { amount: prmdAmount(voucher.claimableNano) });
    } catch {
      // The wallet refused or the user cancelled — nothing to record.
    }
  };

  // One rewards_open per mount, tagged with the visitor's audience state.
  useEffect(() => {
    gaEvent('rewards_open', {
      registered: registered ? 'yes' : 'no',
      entry_point: inMiniApp ? 'telegram' : 'web',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Joins the referral network through the entry contract: one wallet
   *  transaction that registers the profile, issues the permanent key and
   *  pays the PRMD reward on-chain; the unused remainder is refunded.
   *  On EntryClaim, a pending voucher rides the same transaction — the wallet
   *  registers AND collects everything already earned in one payout. */
  const handleGetRefKey = async (quest: QuestItem) => {
    if (!quest.entryAddress) return;
    // Last-line guard against a stale page: re-read the on-chain identity and
    // refuse to send the Enter transaction for an already-registered wallet.
    try {
      const fresh = await readUserByAddress(Address.parse(address));
      if (fresh.registered) return;
    } catch {
      return; // identity unknown — don't risk the user's gas
    }
    // Bot-menu Mini App opens carry no start_param, so fall back to the
    // referrer the bot recorded for this user, then to the one recorded for
    // this wallet's address — same as the games' buy paths.
    const referrerKey = BigInt(
      sanitizeReferrerKey(await resolveOutgoingReferrerKey(address || null)) ?? 0,
    );
    const entryVoucher = quest.entrySchema === 'ec' && voucher?.schema === 'ec' ? voucher : null;
    const body = entryVoucher
      ? beginCell()
          .storeUint(0x45434c31, 32) // EnterWithVoucher
          .storeUint(0n, 64)
          .storeUint(referrerKey, 64)
          .storeCoins(BigInt(entryVoucher.totalNano))
          .storeUint(entryVoucher.validUntil, 32)
          .storeBuffer(Buffer.from(entryVoucher.signature, 'base64'))
          .endCell()
      : beginCell()
          .storeUint(quest.entrySchema === 'ec' ? 0x45434c30 : 0x44495230, 32) // Enter / DirectEnter
          .storeUint(0n, 64)
          .storeUint(referrerKey, 64)
          .endCell();
    try {
      await tonConnectUI.sendTransaction({
        validUntil: entryVoucher
          ? Math.min(entryVoucher.validUntil, Math.floor(Date.now() / 1000) + 300)
          : Math.floor(Date.now() / 1000) + 300,
        messages: [
          {
            address: quest.entryAddress,
            amount: quest.entryValueNano ?? '200000000',
            payload: body.toBoc().toString('base64'),
          },
        ],
      });
      setEntrySent(true);
      if (entryVoucher) void reportRewardsClaimed();
      gaEvent('quest_ref_key_sent', { quest: quest.code });
    } catch {
      // The wallet refused or the user cancelled — nothing to record.
    }
  };

  /** Audience gate: general quests are for everyone, the join quest only for
   *  wallets without a key. Network quests stay visible to everyone but are
   *  locked (see `questLocked`) until the wallet is registered. */
  const questVisible = (quest: QuestItem): boolean => {
    const status = userStatus(quest.claimStatus);
    if (status === 'paid') return false;
    // Earned quests leave the list once the rewards widget can represent them
    // (countdown while maturing, claim/registration button when ready).
    if (status === 'queued' && (voucher != null || pending != null)) return false;
    if (quest.type === 'ref_key' && entrySent) return false;
    // The bot flagged this quest as unearnable for this user (e.g. they were
    // subscribed to the channel before the event, or joined and left).
    if (quest.ineligible) return false;
    switch (quest.type) {
      case 'ref_key':
        // Never offer the join quest to a wallet that may already hold a key:
        // a registered wallet's Enter is rejected on-chain and only burns gas.
        return !registered && identityKnown;
      default:
        return true;
    }
  };

  /** Network quests are teased to unregistered visitors: shown but inactive
   *  until the join quest (registration) unlocks them. */
  const questLocked = (quest: QuestItem): boolean => {
    switch (quest.type) {
      case 'first_referral':
      case 'active_referrals':
      case 'story':
      case 'story_starts':
        return refKey === null;
      default:
        return false;
    }
  };

  const handleStoryShare = async (quest: QuestItem) => {
    if (refKey === null) return;
    // This IS the story quest — always tag the story with the Story source.
    const shared = await shareReferralStory(
      refKey,
      {
        caption: t('share.caption'),
        widgetName: t('refLink.storyWidget'),
        prizeLabel: t('refLink.storyPrizePool'),
      },
      'main',
      true,
    );
    if (!shared) return;
    gaEvent('quest_story_share', { quest: quest.code });
    // Only the trust-based `story` type self-reports a claim; `story_starts`
    // claims are created server-side once enough tagged bot starts arrive.
    if (quest.type !== 'story') return;
    void reportStoryQuestShared(quest.id).then((result) => {
      if (result === 'created' || result === 'exists') {
        setQuests((prev) =>
          Array.isArray(prev)
            ? prev.map((q) => (q.id === quest.id ? { ...q, claimStatus: q.claimStatus ?? 'pending' } : q))
            : prev,
        );
      }
    });
  };

  // On EntryClaim, an unregistered user's pending voucher rides the join
  // transaction itself (claims are for registered wallets): the rewards
  // widget's claim button sends the registration instead, and the join card
  // shows the combined amount: welcome reward + everything already earned.
  const ecEntryQuest = Array.isArray(quests)
    ? quests.find((q) => q.type === 'ref_key' && q.entrySchema === 'ec' && q.entryAddress)
    : undefined;
  const voucherRidesEntry = !registered && voucher?.schema === 'ec' && ecEntryQuest != null;

  /** The join card's headline amount: welcome + pending voucher (if any). */
  const questRewardNano = (quest: QuestItem): string => {
    if (quest.type === 'ref_key' && quest.entrySchema === 'ec' && voucher?.schema === 'ec') {
      return (BigInt(quest.rewardNano) + BigInt(voucher.claimableNano)).toString();
    }
    return quest.rewardNano;
  };

  // A quest disappears from the list once the reward is earned — the rewards
  // widget takes over (countdown, claim button). The queued pill remains only
  // as a fallback while the widget has no data to show.
  const statusPill = (status: UserStatus) => {
    switch (status) {
      case 'rejected':
        return (
          <span className="quest-card__status quest-card__status--rejected">
            <XCircle size={14} aria-hidden="true" /> {t('quests.statusRejected')}
          </span>
        );
      case 'queued':
        return (
          <span className="quest-card__status quest-card__status--queued">
            <Sparkles size={14} aria-hidden="true" /> {t('quests.statusQueued')}
          </span>
        );
      default:
        return null;
    }
  };

  /** Counted quests show live progress toward the goal, e.g. "2/5", using the
   *  same counting rules as the payout scan. */
  const questProgress = (quest: QuestItem, locked: boolean) => {
    if (locked || quest.progressCount == null) return null;
    let target: number;
    let label: string;
    switch (quest.type) {
      case 'story_starts':
        target = quest.minStarts ?? 5;
        label = t('quests.storyProgress');
        break;
      case 'first_referral':
        target = quest.minReferrals ?? 1;
        label = t('quests.registeredRefProgress');
        break;
      case 'active_referrals':
        target = quest.minReferrals ?? 1;
        label = t('quests.refProgress');
        break;
      default:
        return null;
    }
    return (
      <p className="quest-card__progress">
        <Sparkles size={14} aria-hidden="true" /> {label}:{' '}
        <b>
          {Math.min(quest.progressCount, target)}/{target}
        </b>
      </p>
    );
  };

  /** Browser visitors: every Telegram-bound task funnels into the bot with the
   *  visitor's live attribution (ref key / source / click id) carried along. */
  const openInBotAction = (quest: QuestItem) => (
    <div className="quest-card__actions">
      <a
        className="share-btn"
        href={buildAttributedBotLink()}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => gaEvent('quest_open_bot', { quest: quest.code })}
      >
        <Send size={16} aria-hidden="true" /> {t('quests.doInTelegram')}
      </a>
    </div>
  );

  const questAction = (quest: QuestItem, status: UserStatus) => {
    // The reward is earned and waiting — the queued pill replaces the action.
    if (status !== 'available') return null;
    // Joining the network is a pure wallet transaction — it works from the
    // plain website too; everything Telegram-bound funnels into the bot.
    if (!inMiniApp && quest.type !== 'ref_key') return openInBotAction(quest);
    switch (quest.type) {
      case 'subscribe_wallet':
        return (
          <div className="quest-card__actions">
            {!address && (
              <button
                type="button"
                className="share-btn"
                onClick={() => {
                  gaEvent('quest_connect_wallet', { quest: quest.code });
                  void tonConnectUI.openModal();
                }}
              >
                <Wallet size={16} aria-hidden="true" /> {t('quests.connectWallet')}
              </button>
            )}
            {quest.channelUrl && (
              <button
                type="button"
                className="share-btn"
                onClick={() => {
                  gaEvent('quest_subscribe_click', { quest: quest.code });
                  const tg = window.Telegram?.WebApp;
                  if (typeof tg?.openTelegramLink === 'function') {
                    tg.openTelegramLink(quest.channelUrl!);
                  } else {
                    window.open(quest.channelUrl!, '_blank', 'noopener');
                  }
                }}
              >
                <BellRing size={16} aria-hidden="true" /> {t('quests.subscribe')}
              </button>
            )}
          </div>
        );
      case 'first_referral':
      case 'active_referrals':
        return (
          <div className="quest-card__actions">
            <button
              type="button"
              className="share-btn"
              onClick={() => {
                gaEvent('quest_invite_click', { quest: quest.code });
                onGoPartner();
              }}
            >
              <Users size={16} aria-hidden="true" /> {t('quests.inviteFriend')}
            </button>
          </div>
        );
      case 'story':
      case 'story_starts':
        if (!canShareToStory() || refKey === null) return null;
        return (
          <div className="quest-card__actions">
            <button
              type="button"
              className="share-btn share-btn--story"
              onClick={() => void handleStoryShare(quest)}
            >
              <Sparkles size={16} aria-hidden="true" /> {t('quests.shareStory')}
            </button>
          </div>
        );
      case 'ref_key':
        if (entrySent) return null;
        return (
          <div className="quest-card__actions">
            {!address ? (
              <button
                type="button"
                className="share-btn"
                onClick={() => {
                  gaEvent('quest_connect_wallet', { quest: quest.code });
                  void tonConnectUI.openModal();
                }}
              >
                <Wallet size={16} aria-hidden="true" /> {t('quests.connectWallet')}
              </button>
            ) : (
              <button
                type="button"
                className="share-btn"
                onClick={() => {
                  gaEvent('quest_ref_key_click', { quest: quest.code });
                  void handleGetRefKey(quest);
                }}
              >
                <KeyRound size={16} aria-hidden="true" /> {t('quests.getRefKey')}
              </button>
            )}
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <section className="content-section">
      <header className="page-head">
        <h1 className="lottery-title">
          <Gift size={22} aria-hidden="true" /> {t('quests.title')}
        </h1>
        <span className="page-head__sub">{t('quests.subtitle')}</span>
      </header>
      {(voucher != null || pending != null) && (
        <div className="quest-claim">
          <ClaimCoin
            remaining={maturesAt != null ? Math.max(maturesAt - nowSec, 0) : null}
            span={ringWindowRef.current?.span ?? null}
          />
          {claimItems.length > 0 && (
            <ul className="quest-claim__pending">
              {claimItems.map((item, i) => (
                <li
                  key={`${item.questId}-${i}`}
                  className={`quest-claim__pending-item${
                    item.ready ? ' quest-claim__pending-item--ready' : ''
                  }`}
                >
                  <span className="quest-claim__pending-name">
                    {pendingQuestTitle(item.questId)}
                  </span>
                  <span className="quest-claim__pending-amount">
                    +{prmdAmount(item.amountNano)} PRMD
                  </span>
                  {item.ready ? (
                    claimSent || (voucherRidesEntry && entrySent) ? (
                      <span className="quest-claim__pending-time">
                        <CheckCircle2 size={16} aria-hidden="true" />
                      </span>
                    ) : (
                      <button
                        type="button"
                        className="quest-claim__pending-claim"
                        onClick={() => {
                          if (!address) {
                            void tonConnectUI.openModal();
                            return;
                          }
                          gaEvent('quest_claim_click', {});
                          if (voucherRidesEntry && ecEntryQuest) {
                            void handleGetRefKey(ecEntryQuest);
                          } else {
                            void handleClaimRewards();
                          }
                        }}
                      >
                        {address ? (
                          <Gift size={14} aria-hidden="true" />
                        ) : (
                          <Wallet size={14} aria-hidden="true" />
                        )}
                        {voucherRidesEntry ? t('quests.claimRegister') : t('quests.claim')}
                      </button>
                    )
                  ) : (
                    <span className="quest-claim__pending-time">
                      {formatCountdown((item.eligibleAt ?? nowSec) - nowSec)}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
          {voucher ? (
            claimSent || (voucherRidesEntry && entrySent) ? (
              <p className="hint">{t('quests.claimSent')}</p>
            ) : (
              <p className="quest-claim__hint">
                {voucherRidesEntry ? t('quests.claimWithEntryHint') : t('quests.claimHint')}
              </p>
            )
          ) : (
            <p className="quest-claim__hint">{t('quests.maturingHint')}</p>
          )}
        </div>
      )}
      {quests === 'loading' && <p className="hint">{t('quests.loading')}</p>}
      {quests === null && <p className="hint">{t('quests.unavailable')}</p>}
      {Array.isArray(quests) && quests.length === 0 && (
        <p className="hint">{t('quests.empty')}</p>
      )}
      {Array.isArray(quests) &&
        quests.filter(questVisible).map((quest) => {
          const status =
            quest.type === 'ref_key' && entrySent ? 'queued' : userStatus(quest.claimStatus);
          const locked = questLocked(quest);
          return (
            <div className={`quest-card${locked ? ' quest-card--locked' : ''}`} key={quest.id}>
              <div className="quest-card__head">
                <span className="quest-card__reward">
                  <Gift size={15} aria-hidden="true" /> +{prmdAmount(questRewardNano(quest))} PRMD
                </span>
                {statusPill(status)}
              </div>
              <h3 className="quest-card__title">
                {questText(quest.titles, i18n.language) || quest.code}
              </h3>
              {questText(quest.descriptions, i18n.language) && (
                <p className="quest-card__desc">{questText(quest.descriptions, i18n.language)}</p>
              )}
              {questProgress(quest, locked)}
              {locked ? (
                <p className="quest-card__locked">
                  <Lock size={14} aria-hidden="true" /> {t('quests.lockedHint')}
                </p>
              ) : (
                questAction(quest, status)
              )}
            </div>
          );
        })}
      <SupportLink variant="hint" />
    </section>
  );
}
